/**
 * One icon per meaning, everywhere a state is shown (round 2 of the minimal redesign,
 * 12 Oct 2026): the draft table and the phone cards on the case page, the customer line
 * on the mail card, the Action column and the tabs of the queue, and the legend that Help,
 * training module 4 and the cheat sheet print. A sales person learns an icon once.
 *
 * Every name is a canonical export of lucide-react 1.28.0, re-exported by @norican/icons
 * (`History` and `CircleHelp` exist there only as aliases, so they are not used here).
 * Pure data: no React, no case types, so the training may import it (its fence forbids
 * the case-review files, not src/lib).
 */

import {
  ArrowLeftRight,
  Asterisk,
  BadgeCheck,
  Ban,
  Camera,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleQuestionMark,
  CircleX,
  Clock,
  ClockArrowLeft,
  Coins,
  Copy,
  Equal,
  EqualNot,
  Euro,
  Factory,
  FileClock,
  Handshake,
  Hash,
  Hourglass,
  type IconComponent,
  type IconTone,
  Link2,
  ListTodo,
  Mail,
  PackageMinus,
  PackagePlus,
  PackageSearch,
  Paperclip,
  PencilLine,
  RefreshCw,
  Replace,
  ScanLine,
  ScanText,
  SearchCheck,
  Send,
  Sparkles,
  TriangleAlert,
  UserCheck,
  UserRoundCheck,
  UserSearch,
  UserX,
} from "@norican/icons";

import { type DraftGroup, type LineStatusWord } from "@/lib/sales-flow";

// Re-exported so the training practice, whose import fence keeps lib/sales-flow out,
// can type its group headings and status badges from this file alone.
export type { DraftGroup, LineStatusWord };

/** The status icon of a draft line, by its one word (LINE_STATUS_WORDS). */
export const STATUS_ICON: Record<LineStatusWord, IconComponent> = {
  matched: CircleCheck,
  confirm: CircleQuestionMark,
  pick: PackageSearch,
  "no item": Ban,
};

/** No tone on "no item": the ban sign stays grey so the one accent button still wins. */
export const STATUS_TONE: Record<LineStatusWord, IconTone | undefined> = {
  matched: "success",
  confirm: "accent",
  pick: "warning",
  "no item": undefined,
};

/** Where the customer's words come from. */
export const SOURCE_ICON = {
  mail: Mail,
  thread: Mail,
  attachment: Paperclip,
  quotation: Paperclip,
  photo: Camera,
  scan: ScanLine,
} as const satisfies Record<string, IconComponent>;

export type SourceKind = keyof typeof SOURCE_ICON;

/** What the source icon says when it stands alone (its aria label). */
export const SOURCE_LABEL: Record<SourceKind, string> = {
  mail: "From the mail",
  thread: "From an earlier mail of the thread",
  attachment: "From an attachment",
  quotation: "From a quotation",
  photo: "Read from a photo",
  scan: "Read from a scanned page",
};

/** How I2Q found the item (match_basis), plus "person" for a line somebody decided. */
export const BASIS_ICON = {
  exact_number: Hash,
  alias: Link2,
  supersession: Link2,
  repaired_number: Link2,
  // Hunt round 2, decision 6: 97703 written, 0977030 in the catalogue. A suggestion.
  padded_number: Link2,
  // Decision D4 (19 Oct 2026): a number read from a picture, not in the catalogue; one
  // item has the same name as the row. A suggestion.
  same_name: Link2,
  earlier_mail: FileClock,
  existing_quotation: FileClock,
  learned: FileClock,
  model: Sparkles,
  similar: Sparkles,
  person: UserRoundCheck,
} as const satisfies Record<string, IconComponent>;

export type BasisKind = keyof typeof BASIS_ICON;

/** What the basis icon says when it stands alone (its aria label). */
export const BASIS_LABEL: Record<BasisKind, string> = {
  exact_number: "Found by its exact number",
  alias: "Found by an alias",
  supersession: "Found as the successor",
  repaired_number: "Found by a repaired number",
  padded_number: "Found by the US number form",
  same_name: "Found by the same name",
  earlier_mail: "Found in an earlier mail",
  existing_quotation: "Found in an existing quotation",
  learned: "Learned from an earlier correction",
  model: "Best guess from the description",
  similar: "Found by a similar description",
  person: "Decided by a person",
};

/** The band of a price: in band, below the margin, or no price to quote. */
export const PRICE_ICON = {
  in_band: BadgeCheck,
  below_margin: TriangleAlert,
  none: CircleDashed,
} as const satisfies Record<string, IconComponent>;

export type PriceBand = keyof typeof PRICE_ICON;

/**
 * A price a person set (round 4, 12 Oct 2026): the same person-with-a-check as a decided
 * item, so "a person did this" reads the same in the I2Q proposes and the Price column.
 */
export const MANUAL_PRICE_ICON: IconComponent = UserRoundCheck;

/**
 * The lead time (sales review of 12 Oct 2026, P-02): one F&O holds today, one from an
 * earlier quotation or mail offer (context, never a promise), none in F&O, or not
 * looked up yet.
 */
export const LEAD_ICON = {
  known: Clock,
  // `History` is only an alias in lucide-react; the canonical clock with an arrow back.
  historic: ClockArrowLeft,
  none: CircleDashed,
  not_looked_up: Hourglass,
} as const satisfies Record<string, IconComponent>;

export type LeadState = keyof typeof LEAD_ICON;

/** The alert on a quantity that needs a look. */
export const QTY_ISSUE_ICON: IconComponent = CircleAlert;

/**
 * The mark on a quantity I2Q used because the mail gave none (product owner, 14 Oct
 * 2026): the quantity is 1, the reply asks the customer to confirm it.
 */
export const QTY_DEFAULT_ICON: IconComponent = Asterisk;

/** The customer line on the mail card: confirmed, suggested, or nobody matched. */
export const CUSTOMER_ICON = {
  certain: UserCheck,
  suggested: UserSearch,
  none: UserX,
} as const satisfies Record<string, IconComponent>;

/**
 * The customer context under the customer line of an agent case (U-02, 12 Oct 2026):
 * the sender who is the agent, and the end customer the mail names.
 */
export const PARTY_ICON = {
  agent: Handshake,
  endCustomer: Factory,
} as const satisfies Record<string, IconComponent>;

/**
 * The deeper search on the case page (round 2, 13 Oct 2026): a proposed account or item,
 * an item shown beside the item of a line, and "Look again". Each icon sits next to its
 * word, so it is decorative; the ten lines of the legend below do not change.
 */
export const INVESTIGATION_ICON = {
  proposal: SearchCheck,
  alternative: Replace,
  lookAgain: RefreshCw,
} as const satisfies Record<string, IconComponent>;

/**
 * "Learn from sales" (20 Oct 2026): the result of one row of the line difference, the I2Q
 * draft against what sales quoted in F&O. The page, its drawer and the "Quoted in F&O"
 * block of the case page show the same icon next to the same word. The same item is the
 * green check of "matched"; a quantity that differs is the alert of a quantity that needs a
 * look; a draft line that sales did not quote and that has no item is the ban sign of "no
 * item". The other three are new and mean only this.
 */
export const DIFF_ICON = {
  same_item: CircleCheck,
  other_item: ArrowLeftRight,
  missed: PackageMinus,
  extra: PackagePlus,
  qty_differs: CircleAlert,
  price_differs: Coins,
  not_quoted: Ban,
} as const satisfies Record<string, IconComponent>;

export type DiffVerdict = keyof typeof DIFF_ICON;

/**
 * "Learn from sales": the customer of the I2Q draft against the account of the quotation.
 * Equal and not equal; "I2Q had no customer" is the person with a cross of "nobody
 * matched"; no account in F&O is the dashed circle of "nothing there".
 */
export const CUSTOMER_MATCH_ICON = {
  same: Equal,
  different: EqualNot,
  i2q_none: UserX,
  no_fo: CircleDashed,
} as const satisfies Record<string, IconComponent>;

/** The icon on each group row of the draft (DRAFT_GROUPS). */
export const GROUP_ICON: Record<DraftGroup, IconComponent> = {
  "Needs you": ListTodo,
  Matched: CircleCheck,
  "Nothing to quote": Ban,
};

export const GROUP_TONE: Record<DraftGroup, IconTone | undefined> = {
  "Needs you": "warning",
  Matched: "success",
  "Nothing to quote": undefined,
};

/** The queue's action words (QUEUE_ACTION_WORDS). "Reading" keeps the DS Spinner, so it has no entry. */
export const QUEUE_ICON = {
  "Could not read": CircleX,
  "Nothing to do": CircleCheck,
  "Find customer": UserX,
  Confirm: UserSearch,
  Pick: PackageSearch,
  Check: TriangleAlert,
  "Prepare reply": PencilLine,
  "Copy reply": Copy,
} as const satisfies Record<string, IconComponent>;

/** The icons next to the tab labels "To review" and "Closed" of the queue; "Ready to quote" has none. */
export const TAB_ICON = {
  "To review": ListTodo,
  Closed: CircleCheck,
} as const satisfies Record<string, IconComponent>;

/**
 * The seven stations of the "How it works" figure (round 3), from the mail arriving to
 * the reply leaving Outlook. Help, module 1 and the quick start draw the same strip.
 */
export const FLOW_ICON = {
  arrives: Mail,
  reads: ScanText,
  finds: UserSearch,
  prices: Euro,
  prepares: PencilLine,
  checks: UserRoundCheck,
  sends: Send,
} as const satisfies Record<string, IconComponent>;

export type FlowStation = keyof typeof FLOW_ICON;

export interface LegendEntry {
  /** The first icon of the line; the help shows the others next to it. */
  icon: IconComponent;
  /** Every icon the line names, in the order the sentence names them. */
  icons: readonly IconComponent[];
  /** One line, as printed under "What the icons mean". */
  meaning: string;
  /** Where the icon sits on the screen. */
  where: string;
}

function entry(icons: readonly IconComponent[], meaning: string, where: string): LegendEntry {
  return { icon: icons[0] as IconComponent, icons, meaning, where };
}

/** The ten lines of "What the icons mean", in the order Help, module 4 and the cheat sheet print them. */
export const LEGEND: readonly LegendEntry[] = [
  entry(
    [STATUS_ICON.matched],
    "Green check: matched. I2Q is sure. The big button accepts these rows; a decided row needs nothing more.",
    "Status column",
  ),
  entry(
    [STATUS_ICON.confirm],
    "Blue question mark: confirm. A good guess, press Confirm in the row.",
    "Status column",
  ),
  entry(
    [STATUS_ICON.pick],
    "Orange box with a magnifier: pick. No sure item, press Pick item and pick one.",
    "Status column",
  ),
  entry(
    [STATUS_ICON["no item"]],
    "Grey ban sign: no item. Somebody said there is no item for this line.",
    "Status column",
  ),
  entry(
    [SOURCE_ICON.mail, SOURCE_ICON.attachment, SOURCE_ICON.photo, SOURCE_ICON.scan],
    "Envelope, paperclip, camera, scan: where the customer's words come from (mail, attachment, photo, scanned page).",
    "Customer asked column",
  ),
  entry(
    [
      BASIS_ICON.exact_number,
      BASIS_ICON.alias,
      BASIS_ICON.earlier_mail,
      BASIS_ICON.similar,
      BASIS_ICON.person,
    ],
    "Hash, link, clock on a page, sparkles: how I2Q found the item (exact number, alias or successor, history, similar description). A person with a check: you or a colleague picked it.",
    "I2Q proposes column",
  ),
  entry(
    [PRICE_ICON.in_band, PRICE_ICON.below_margin, PRICE_ICON.none, MANUAL_PRICE_ICON],
    "Check badge on a price: in band. Warning triangle: below margin, a check holds the case. Dashed circle: no price, the cell says why. A person with a check: you or a colleague set the price.",
    "Price column",
  ),
  entry(
    [LEAD_ICON.known, LEAD_ICON.historic, LEAD_ICON.none, LEAD_ICON.not_looked_up],
    "Clock: a lead time F&O holds today. From the item master, the cell names that source under the figure. Clock with an arrow: a lead time from an earlier quotation, context only. Dashed circle: no lead time in F&O. Hourglass: not looked up yet.",
    "Lead column",
  ),
  entry(
    [QTY_ISSUE_ICON, QTY_DEFAULT_ICON],
    "Alert circle on a quantity: the quantity needs a look. Asterisk: the mail gave no quantity, so I2Q used 1.",
    "Qty column",
  ),
  entry(
    [QUEUE_ICON.Pick, QUEUE_ICON.Confirm, QUEUE_ICON.Check, QUEUE_ICON["Copy reply"]],
    "In the queue the same icon sits in front of the action word.",
    "Queue, Action column",
  ),
];
